import { IconButton, InputAdornment, Stack, styled, Typography } from '@mui/material';
import { Icon_Arrow } from 'assets';
import { ButtonCustom } from 'app/components/elements/ButtonCustom';
import { InputText } from 'app/components/elements/InputText';
import React, { ChangeEvent, useState } from 'react';
import { StyledParams } from 'types/common';
import { InputSelect, SelectOptions } from 'app/components/elements/InputSelect';
import { SelectChangeEvent } from '@mui/material/Select';
import { RoomInfo } from 'types/room';
import UploadOutlinedIcon from '@mui/icons-material/UploadOutlined';
import { Close } from '@mui/icons-material';

type HeaderClientProps = {
  paths: RoomInfo;
  textButton?: string;
  onClickButton?: () => void;
  onSearch?: (valueSearch: string) => void;
  onFilterType?: (valueFilter: string) => void;
  optionFilterType?: SelectOptions[];
  onFilterClient?: (valueFilter: string) => void;
  optionFilterClient?: SelectOptions[];
  valueFilterType?: string;
  valueFilterClient?: string;
  title?: string;
  buttons?: {
    textButton: string;
    onClick: () => void;
    type: string;
  }[];
  uploadPlanButton?: {
    label: string;
    onClick: () => void;
  };
  onShowLastRoom?: () => void;
  isCanAccessRoom?: boolean;
  isSearch?: boolean;
  searchholder: string;
};

export const HeaderClient = ({
  textButton,
  paths,
  onSearch,
  onFilterType,
  optionFilterType,
  onFilterClient,
  optionFilterClient,
  valueFilterType,
  valueFilterClient,
  onShowLastRoom,
  searchholder,
  title,
  buttons,
  isCanAccessRoom,
  uploadPlanButton,
  isSearch
}: HeaderClientProps) => {
  const [valueSearch, setValueSearch] = useState<string>('');
  return (
    <Stack>
      <Wrapper justifyContent={textButton ? 'space-between' : 'flex-start'}>
        <Stack direction="row" spacing={1.5}>
          <TextPath>Path:</TextPath>
          {paths.map((path, index) => (
            <Stack
              key={index}
              flexDirection={'row'}
              alignItems={'center'}
              justifyContent={'center'}
              gap={2}
              mr={0.5}>
              <TextPath
                active={paths.length === index + 1 ? 'active' : 'inactive'}
                onClick={() => {
                  path?.onClick && path?.onClick();
                }}>
                {path.name}
              </TextPath>
              {index < paths.length - 1 && (
                <Icon_Arrow color={paths.length === index + 2 ? '#226AAA' : '#818181'} />
              )}
            </Stack>
          ))}
        </Stack>
        <Stack flexDirection={'row'} gap={2}>
          {isCanAccessRoom && onShowLastRoom && (
            <ButtonCustom
              sx={{ width: 'max-content', height: '46px', padding: '0 20px' }}
              label={'Last Location'}
              onClick={onShowLastRoom}
            />
          )}
        </Stack>
      </Wrapper>
      <Wrapper justifyContent="space-between" alignItems="center">
        {title && <TitlePageStyled>{isSearch ? 'Search Location' : title}</TitlePageStyled>}

        {uploadPlanButton && (
          <ButtonCustom
            label={uploadPlanButton.label}
            onClick={uploadPlanButton.onClick}
            iconStart={
              <UploadOutlinedIcon fontSize="small" sx={{ color: '#fff', marginRight: '2px' }} />
            }
            sx={{
              width: 'max-content',
              height: '46px',
              px: '20px'
            }}
          />
        )}
      </Wrapper>
      <Stack alignItems={'flex-start'} justifyContent={'space-between'} flexDirection={'row'}>
        <Stack alignItems={'center'} gap={2.5}>
          {onFilterClient && optionFilterClient && (
            <InputSelect
              value={valueFilterClient}
              placeholder={'Filter by Asset Type'}
              onChange={(e: SelectChangeEvent<unknown>) => {
                onFilterClient(e.target.value as string);
              }}
              sx={{ width: '352px', minHeight: '46px', height: '46px', padding: 0 }}
              options={optionFilterClient}
            />
          )}
          {onSearch && (
            <Stack flexDirection={'row'} gap={1.25}>
              <Stack gap={1}>
                <InputText
                  startAdornment={
                    <InputAdornment position="end">
                      <IconButton>
                        <img alt={'icon-user'} src={'/icons/icon_search.svg'} width={24} />
                      </IconButton>
                    </InputAdornment>
                  }
                  endAdornment={
                    valueSearch && (
                      <InputAdornment position="start">
                        <IconButton
                          onClick={() => {
                            setValueSearch('');
                            onSearch('');
                          }}>
                          <Close />
                        </IconButton>
                      </InputAdornment>
                    )
                  }
                  placeholder={searchholder}
                  inputProps={{
                    value: valueSearch,
                    style: {
                      width: '352px',
                      minHeight: '46px',
                      height: '46px',
                      padding: 0
                    },
                    onChange: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                      setValueSearch(event.target.value);
                    },
                    onKeyDown: (
                      event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
                    ) => {
                      if (event.key === 'Enter') {
                        onSearch && onSearch(valueSearch);
                      }
                    }
                  }}
                />
              </Stack>
              <ButtonCustom
                sx={{ width: 'max-content', height: '46px' }}
                label={'Search'}
                onClick={() => {
                  onSearch(valueSearch);
                }}
              />
            </Stack>
          )}
        </Stack>
        <Stack alignItems={'flex-end'} gap={'20px'}>
          {onFilterType && (
            <InputSelect
              value={valueFilterType}
              placeholder={'Filter by Asset Type'}
              onChange={(e: SelectChangeEvent<unknown>) => {
                onFilterType(e.target.value as string);
              }}
              sx={{ width: '352px', minHeight: '46px', height: '46px', padding: 0 }}
              options={optionFilterType}
            />
          )}
          <Stack flexDirection={'row'} gap={'10px'}>
            {buttons?.length !== 0 &&
              buttons?.map((item, index) => (
                <ButtonCustom
                  key={index}
                  isSecondary={item.type === 'secondary'}
                  sx={{ width: 'max-content', height: '46px', padding: '0 20px' }}
                  label={item.textButton}
                  onClick={item.onClick}
                />
              ))}
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};

const Wrapper = styled(Stack)(() => ({
  flexDirection: 'row',
  alignItems: 'center',
  width: '100%'
}));

const TextPath = styled(Typography)(({ active }: { active?: string }) => ({
  fontSize: '16px',
  fontWeight: active === 'active' ? 700 : 400,
  color: active === 'active' ? '#226AAA' : '#818181',
  cursor: 'pointer',
  fontFamily: `'Avenir',sans-serif`,
  textDecoration: 'none'
}));
const TitlePageStyled = styled(Typography)(({ theme }: StyledParams) => ({
  margin: '20px 0',
  color: theme.colors.base,
  fontSize: '24px',
  fontWeight: 900,
  fontFamily: `'Avenir',sans-serif`
}));
